import {
  CalendarDate,
  parseAbsolute,
  toCalendarDate,
  toZoned,
  type ZonedDateTime,
} from '@internationalized/date'

/**
 * Période budgétaire : un mois, identifié par `AAAA-MM`, qui commence le jour `startDay`
 * (1 à 28) à minuit, heure locale de l'utilisateur. Avec `startDay = 25`, la période `2026-10`
 * va du 25 octobre au 24 novembre inclus.
 */

export type PeriodId = string

export interface PeriodSettings {
  /** Fuseau IANA, par exemple `Africa/Porto-Novo`. */
  timeZone: string
  /** Jour de début du mois budgétaire, de 1 à 28 (le 29 n'existe pas tous les mois). */
  startDay: number
}

export interface PeriodBounds {
  id: PeriodId
  /** Premier jour inclus (date locale). */
  firstDay: CalendarDate
  /** Dernier jour inclus (date locale). */
  lastDay: CalendarDate
  /** Instant de début, inclus (ISO 8601 UTC). */
  startsAt: string
  /** Instant de fin, exclu (ISO 8601 UTC). */
  endsAt: string
}

const PERIOD_ID = /^(\d{4})-(0[1-9]|1[0-2])$/

export function assertPeriodSettings(settings: PeriodSettings): void {
  if (!Number.isInteger(settings.startDay) || settings.startDay < 1 || settings.startDay > 28) {
    throw new RangeError(`startDay doit être un entier de 1 à 28 (reçu : ${settings.startDay})`)
  }
}

export function periodIdFrom(year: number, month: number): PeriodId {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`
}

export function parsePeriodId(id: PeriodId): { year: number; month: number } {
  const match = PERIOD_ID.exec(id)
  if (match === null) throw new RangeError(`Identifiant de période invalide : ${id}`)
  return { year: Number(match[1]), month: Number(match[2]) }
}

export function shiftPeriod(id: PeriodId, months: number): PeriodId {
  const { year, month } = parsePeriodId(id)
  const shifted = new CalendarDate(year, month, 1).add({ months })
  return periodIdFrom(shifted.year, shifted.month)
}

/** Période à laquelle appartient une date locale. */
export function periodOfDate(date: CalendarDate, settings: PeriodSettings): PeriodId {
  assertPeriodSettings(settings)
  const anchor = date.day >= settings.startDay ? date : date.subtract({ months: 1 })
  return periodIdFrom(anchor.year, anchor.month)
}

/** Période à laquelle appartient un instant (ISO 8601), dans le fuseau de l'utilisateur. */
export function periodOfInstant(instant: string, settings: PeriodSettings): PeriodId {
  return periodOfDate(localDateOf(instant, settings.timeZone), settings)
}

export function localDateOf(instant: string, timeZone: string): CalendarDate {
  return toCalendarDate(parseAbsolute(instant, timeZone))
}

function startOfDay(date: CalendarDate, timeZone: string): ZonedDateTime {
  return toZoned(date, timeZone)
}

export function periodBounds(id: PeriodId, settings: PeriodSettings): PeriodBounds {
  assertPeriodSettings(settings)
  const { year, month } = parsePeriodId(id)
  const firstDay = new CalendarDate(year, month, settings.startDay)
  const nextFirstDay = firstDay.add({ months: 1 })
  return {
    id,
    firstDay,
    lastDay: nextFirstDay.subtract({ days: 1 }),
    startsAt: startOfDay(firstDay, settings.timeZone).toAbsoluteString(),
    endsAt: startOfDay(nextFirstDay, settings.timeZone).toAbsoluteString(),
  }
}

/** Nombre de jours de la période. */
export function periodLength(bounds: PeriodBounds): number {
  return bounds.lastDay.compare(bounds.firstDay) + 1
}

/**
 * Jours restants dans la période, aujourd'hui compris. 0 si la période est terminée,
 * la longueur totale si elle n'a pas commencé.
 */
export function daysRemaining(bounds: PeriodBounds, today: CalendarDate): number {
  if (today.compare(bounds.lastDay) > 0) return 0
  if (today.compare(bounds.firstDay) < 0) return periodLength(bounds)
  return bounds.lastDay.compare(today) + 1
}

/** Jours entièrement écoulés dans la période, avant aujourd'hui. */
export function daysElapsed(bounds: PeriodBounds, today: CalendarDate): number {
  return periodLength(bounds) - daysRemaining(bounds, today)
}
